import { useMemo } from 'react';
import { useMeQuery } from '@/api/user';
import { InvitationData } from '@/types';

const useMeInvitations = (): {
  data: InvitationData[];
  isLoading: boolean;
} => {
  const { data: me, isLoading } = useMeQuery();

  const invitations: InvitationData[] = useMemo(() => {
    if (!isLoading && me) {
      return [
        // Legacy friend pending memberships.
        ...me.memberships
          .filter(
            ({ status, invitationExpired }) =>
              status === 'pending' && !invitationExpired,
          )
          .map((membership): InvitationData => ({
            source: 'friend',
            token: membership.invitationToken ?? '',
            organisationName: membership.organisationName,
            avatar: membership.organisationAvatar,
            invitedBy: membership.inviterName ?? '',
            expiresAt: membership.invitationExpiresAt ?? '',
            canBeAccepted: membership.invitationCanBeAccepted,
          })),
        // Diplomat invitation records. Whether an accept sticks (e.g. an
        // SSO-mandating organisation) is answered at accept time, not here.
        ...(me.invitations ?? []).map((invitation): InvitationData => ({
          source: 'diplomat',
          token: invitation.token,
          organisationName: invitation.organisationName,
          invitedBy: invitation.invitedBy,
          expiresAt: invitation.expiresAt,
          canBeAccepted: true,
        })),
      ];
    } else {
      return [];
    }
  }, [me, isLoading]);

  const result = useMemo(
    () => ({
      data: invitations,
      isLoading: isLoading,
    }),
    [invitations, isLoading],
  );

  return result;
};

export { useMeInvitations };
